import { ChevronLeft, ChevronRight, FirstPage, LastPage } from '@mui/icons-material';
import styled from '@emotion/styled';
import { uiTheme as t } from './theme';

/** Tamaños de página que ofrece el selector cuando nadie pide otros. */
export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export interface PaginationLabels {
  /** "Mostrando" */
  showing: string;
  /** "de" */
  of: string;
  /** "Página" */
  page: string;
  /** aria-label del botón anterior */
  prev: string;
  /** aria-label del botón siguiente */
  next: string;
  /** aria-label del botón de primera página. Solo si hay selector de tamaño. */
  first?: string;
  /** aria-label del botón de última página. */
  last?: string;
  /** Etiqueta del selector de tamaño, p. ej. "Por página". */
  perPage?: string;
}

interface PaginationProps {
  page: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  labels: PaginationLabels;
  onChange: (page: number) => void;
  /**
   * Habilita el selector de tamaño. Sin este callback la barra se pinta igual
   * que antes: la pantalla que no deja elegir tamaño no enseña el control.
   */
  onPageSizeChange?: (pageSize: number) => void;
  /** Opciones del selector. Por omisión, 10 / 25 / 50 / 100. */
  pageSizeOptions?: number[];
}

const Bar = styled.div({
  marginTop: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  flexWrap: 'wrap',
  fontFamily: t.font,
  fontSize: 13.5,
  color: t.color.muted,
});

const Left = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  flexWrap: 'wrap',
});

const Controls = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
});

const PageLabel = styled.span({
  color: t.color.text,
  fontWeight: 500,
});

// Es un `<label>`: al pulsar el texto se abre el desplegable, igual que en
// SearchBox. Sin esto el área sensible es solo la caja del select.
const SizePicker = styled.label({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  cursor: 'pointer',
});

const SizeSelect = styled.select({
  fontFamily: t.font,
  fontSize: 13.5,
  color: t.color.text,
  padding: '6px 8px',
  borderRadius: t.radius.xs,
  border: `1px solid ${t.color.inputBorder}`,
  background: t.color.surface,
  cursor: 'pointer',
  '&:focus': { outline: 'none', borderColor: t.color.primary },
});

const NavButton = styled.button({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 34,
  height: 34,
  borderRadius: t.radius.xs,
  border: `1px solid ${t.color.inputBorder}`,
  background: t.color.surface,
  color: t.color.text,
  cursor: 'pointer',
  transition: 'border-color 0.15s ease, color 0.15s ease, background 0.15s ease',
  '& .MuiSvgIcon-root': { fontSize: 20 },
  '&:hover:not(:disabled)': { borderColor: t.color.primary, color: t.color.primary },
  '&:disabled': { opacity: 0.45, cursor: 'not-allowed' },
});

/**
 * Molécula: barra de paginación. A la izquierda el rango mostrado y, si la
 * pantalla lo permite, el selector de cuántos renglones enseñar. A la derecha
 * el indicador de página y la navegación. Los números se pintan directo en el
 * JSX (sin pasar por params de traducción).
 *
 * Cambiar el tamaño devuelve a la primera página: quien está en la 7 con 10 por
 * página y pide 100 se saldría del rango, y la tabla aparecería vacía.
 */
const Pagination = ({
  page,
  pageSize,
  totalPages,
  totalItems,
  labels,
  onChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
}: PaginationProps) => {
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalItems);

  // El tamaño en uso puede no estar entre las opciones: una pantalla puede
  // arrancar en 20 y ofrecer 10/25/50/100. Se añade para que el select no
  // muestre otro valor del que realmente se está usando.
  const options = pageSizeOptions.includes(pageSize)
    ? pageSizeOptions
    : [...pageSizeOptions, pageSize].sort((a, b) => a - b);

  return (
    <Bar>
      <Left>
        <span>
          {labels.showing} {from}–{to} {labels.of} {totalItems}
        </span>
        {onPageSizeChange ? (
          <SizePicker>
            {labels.perPage ? <span>{labels.perPage}</span> : null}
            <SizeSelect value={pageSize} onChange={event => onPageSizeChange(Number(event.target.value))}>
              {options.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </SizeSelect>
          </SizePicker>
        ) : null}
      </Left>
      <Controls>
        <PageLabel>
          {labels.page} {page} {labels.of} {totalPages}
        </PageLabel>
        {onPageSizeChange ? (
          <NavButton type="button" aria-label={labels.first} disabled={page <= 1} onClick={() => onChange(1)}>
            <FirstPage />
          </NavButton>
        ) : null}
        <NavButton type="button" aria-label={labels.prev} disabled={page <= 1} onClick={() => onChange(page - 1)}>
          <ChevronLeft />
        </NavButton>
        <NavButton
          type="button"
          aria-label={labels.next}
          disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
        >
          <ChevronRight />
        </NavButton>
        {onPageSizeChange ? (
          <NavButton
            type="button"
            aria-label={labels.last}
            disabled={page >= totalPages}
            onClick={() => onChange(totalPages)}
          >
            <LastPage />
          </NavButton>
        ) : null}
      </Controls>
    </Bar>
  );
};

export default Pagination;
